import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { isMobile } from "react-device-detect";
import { useMediaQuery } from "usehooks-ts";

/* 
I have to distinguish between mobile screen as in size vs an actual mobile device in this component. The reason is, that I am using onTouchEnd for the mobile device and onMouseEnter for the desktop device.
*/

type Props = {
  onChange: (isActive: boolean) => void;
  onClick?: () => void;
  isActive: boolean;
  disabled?: boolean;
  locked?: boolean;
  size?: "small" | "medium" | "large";
  activeColor?: string;
};

export const Switch = ({
  onChange,
  onClick,
  isActive,
  disabled,
  locked = false,
  size = "medium",
  activeColor,
}: Props) => {
  /* Controlled: the knob shows the prop and nothing else, so a change the
     owner refuses (a club setting with no club to read) never shows as on. */
  const isMobileScreen = useMediaQuery(BREAKPOINTS.MOBILE.query);

  const circleStyle = {
    transition: "transform 0.15s ease-in-out", // Reduced from 0.2s for snappier feel
    transform: isActive
      ? isMobileScreen && size === "large"
        ? "translateX(78%)"
        : "translateX(80%)"
      : "translateX(0%)",
    boxShadow: "0px 1px 1px 0px rgba(0, 0, 0, 0.25)",
  };

  const handleToggle = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (locked) {
      onChange(true);
      return;
    }
    onClick?.();
    if (disabled) return;

    onChange(!isActive);
  };

  return (
    <div
      className={cn(
        "flex items-center rounded-full p-1 cursor-pointer",
        size === "small"
          ? "w-7 h-4"
          : isMobileScreen && size === "large"
            ? "w-12 h-8"
            : "w-10 h-6",
        isActive ? (activeColor ?? "bg-primary-blue") : "bg-[#49535F]",
      )}
      style={{
        boxShadow: "0px 1px 1px 0px rgba(0, 0, 0, 0.25) inset",
        // Add touch-action for better mobile performance
        touchAction: "manipulation",
      }}
      onClick={(e) => {
        if (!isMobile) {
          handleToggle(e);
        }
      }}
      onTouchEnd={handleToggle}
    >
      <div
        style={circleStyle}
        className={cn(
          "rounded-full shadow-md bg-white -ml-[2px]",
          size === "small"
            ? "w-3.5 h-3.5"
            : isMobileScreen && size === "large"
              ? "w-6 h-6"
              : "w-5 h-5",
        )}
      />
    </div>
  );
};
